import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  RendererFactory2,
  inject,
  input,
  signal,
} from "@angular/core";
import { MAX_SAFE_INTEGER } from "src/config";
import { getOuterWidth } from "src/standard/ts/dom/getOuterWidth";
import invariant from "tiny-invariant";

function safeInt(val: number | string) {
  if (typeof val === "number") {
    return val;
  }

  const parsed = Number.parseInt(val, 10);
  if (isNaN(parsed)) {
    return 0;
  }

  return parsed;
}

@Component({
  standalone: true,
  selector: "standard-resize-side",
  template: ``,
  host: {
    "[class]": "side()",
    "(mousedown)": "onInitResize($event)",
  },
  styles: `
    :host {
      position: absolute;
      display: block;
      box-sizing: border-box;
      border: 0;
      background-color: transparent;

      & > button {
        box-sizing: border-box;
        border: 0;
        background-color: transparent;
      }

      top: -1px;
      bottom: -1px;
      cursor: col-resize !important;

      &:hover {
        background-color: var(--bs-tertiary-color);
      }

      &.start,
      &.end {
        width: 6px;
      }

      &.start {
        inset-inline-start: -3px;
      }
      &.end {
        inset-inline-end: -3px;
      }

      // Increase hit area
      &::before {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 6px;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StandardResizeSideComponent implements OnInit, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly document = inject<Document>(DOCUMENT);

  private readonly rendererFactory = inject(RendererFactory2);
  private readonly zone = inject(NgZone);

  constructor() {
    this.window = this.document.defaultView as Window;
  }

  private renderer = this.rendererFactory.createRenderer(null, null);
  private window: Window;

  readonly _target = input.required<HTMLElement | string>({ alias: "target" });
  readonly side = input<"start" | "end">("start");
  readonly property = input<string>("width");
  readonly minWidth = input<number>(0);
  readonly maxWidth = input<number>(MAX_SAFE_INTEGER);

  readonly target = signal<HTMLElement | undefined>(undefined);

  private documentResizeListener: (() => void) | undefined;
  private documentResizeEndListener: (() => void) | undefined;

  private readonly isResizing = signal<boolean>(false);
  private lastPageX: number | undefined;

  ngOnInit() {
    this.bindGlobalListeners();
  }

  ngOnDestroy() {
    this.unbindGlobalListeners();
  }

  private bindGlobalListeners() {
    this.bindDocumentResizeListeners();
  }

  private unbindGlobalListeners() {
    this.unbindDocumentResizeListeners();
  }

  private bindDocumentResizeListeners() {
    if (!this.documentResizeListener && !this.documentResizeEndListener) {
      this.zone.runOutsideAngular(() => {
        this.documentResizeListener = this.renderer.listen(
          this.window,
          "mousemove",
          this.onResize.bind(this),
        );
        this.documentResizeEndListener = this.renderer.listen(
          this.window,
          "mouseup",
          this.resizeEnd.bind(this),
        );
      });
    }
  }

  private unbindDocumentResizeListeners() {
    if (this.documentResizeListener && this.documentResizeEndListener) {
      this.documentResizeListener();
      this.documentResizeEndListener();
      this.documentResizeListener = undefined;
      this.documentResizeEndListener = undefined;
    }
  }

  onInitResize(event: MouseEvent) {
    // Ignore right-clicks and clicks with a modifier key
    if (
      event.button === 2 ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.metaKey
    ) {
      return;
    }

    if (this.isResizing()) {
      return;
    }

    const _target = this._target();
    if (typeof _target === "string") {
      const parent = this.host.nativeElement.closest(_target) as
        | HTMLElement
        | undefined;
      invariant(parent);
      this.target.set(parent);
    } else {
      this.target.set(_target);
    }

    this.document.body.classList.add("p-unselectable-text");

    this.isResizing.set(true);
    this.lastPageX = event.pageX;
  }

  onResize(event: MouseEvent) {
    const isResizing = this.isResizing();
    const minWidth = this.minWidth();
    const maxWidth = this.maxWidth();
    if (!isResizing || this.lastPageX === undefined) {
      return;
    }

    // Ignore, resizing without holding a button, right-clicks, and clicks with a modifier key
    if (
      event.buttons === 0 ||
      event.button === 2 ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.metaKey
    ) {
      // bail out
      this.resizeEnd();
      return;
    }

    let deltaX = event.pageX - this.lastPageX;
    if (globalThis.__rtl) {
      deltaX = deltaX * -1;
    }

    let startWidth: number;
    const target = this.target();
    invariant(target);

    const property = this.property();

    switch (property) {
      case "width":
      case "max-width":
        startWidth = getOuterWidth(target);
        break;

      default:
        {
          // get css variable value
          const cssVar = target.style.getPropertyValue(property);
          startWidth = safeInt(cssVar);
        }
        break;
    }

    const side = this.side();
    let newWidth = side === "start" ? startWidth - deltaX : startWidth + deltaX;

    // Disallow resizing to get too small
    if (newWidth < minWidth) {
      newWidth = minWidth;
    }

    // Disallow resizing to get too big
    if (newWidth > maxWidth) {
      newWidth = maxWidth;
    }

    switch (property) {
      case "width":
        target.style.width = `${newWidth}px`;
        break;

      case "max-width":
        target.style.maxWidth = `${newWidth}px`;
        break;

      default:
        // set css variable value
        target.style.setProperty(property, `${newWidth}px`);

        break;
    }

    this.lastPageX = event.pageX;
  }

  resizeEnd() {
    const isResizing = this.isResizing();
    if (!isResizing) {
      return;
    }

    this.isResizing.set(false);

    this.document.body.classList.remove("p-unselectable-text");
  }
}
